import { format } from "date-fns";
import { orderBy } from "lodash";
import { getPayFrequencyName, toDecimal } from "../validation/Validators";

const today = format(new Date(), "yyyy-MM-dd");
const formatStartDate = (date) => (date ? format(new Date(date), "do MMM, yyyy") : "Unknown Start Date");
const formatEndDate = (date) => (date && date != "null" ? format(new Date(date), "do MMM, yyyy") : "Now");

const roundTo2 = toDecimal(2, 2, "stripIfInteger");

export const mapRolesForDropdown = (roles = [], agreement) => {
	const { engagementDate, terminationDate } = agreement;
	return roles?.map((role) => {
		const { id, name, start, end } = { ...role };
		let endDate = formatEndDate(end);
		// end can be 'null' or undefined, if a termination is set and the role is either 'null' or undefined, then we need to use the termination date
		if (["null", undefined].includes(end)) {
			endDate = formatEndDate(terminationDate);
		}

		const startDate = formatStartDate(start ? start : engagementDate);

		return {
			value: id,
			label: `${startDate} - ${endDate} | ${name}`,
			occupation: role.occupation,
		};
	});
};

export const mapDepartmentsForDropdown = (departments = [], departmentOptions = []) => {
	return departments?.map((item) => {
		const { id, department, start: startDate, end: endDate } = { ...item };

		const name = departmentOptions?.find(({ value }) => value == department)?.label;
		const start = formatStartDate(startDate);
		const end = formatEndDate(endDate);

		return {
			value: id,
			label: `${start} - ${end} | ${name}`,
			department,
		};
	});
};

export const mapSalariesForDropdown = (salaries = [], currency) => {
	return salaries.map((s, sIndex) => {
		const start = formatStartDate(s.start);
		const end = formatEndDate(s.end);
		const payFrequency = getPayFrequencyName(s.payPeriodFrequency);

		return {
			label: `${start} - ${end} | ${currency}${roundTo2(parseFloat(s.salaryAmount))} per ${payFrequency}`,
			value: s.id,
			...s,
		};
	});
};

export const mapPensionsForDropdown = (pensionPlans = [], groups) => {
	return pensionPlans.map((p) => {
		const { id, start: startDate, end: endDate, pensionGroup, assessmentDate } = p;
		const start = formatStartDate(startDate);
		const end = formatEndDate(endDate);
		let groupLabel = groups?.find(({ id }) => id === pensionGroup)?.name

		if (!groupLabel) {
			groupLabel = (!pensionGroup && assessmentDate) ? "Assessed (not enrolled)" : "Deleted Group";
		}


		return {
			label: `${start} - ${end} | ${groupLabel}`,
			value: id,
			data: p,
		};
	});
};

export const mapTaxesForDropdown = (taxes = []) => {
	return taxes.map((s, sIndex) => {
		const start = s.startDate ? format(new Date(s.startDate), "do MMM yyyy") : "Unknown Start Date";
		const end = s.endDate && s.endDate != "null" ? format(new Date(s.endDate), "do MMM yyyy") : "Now";

		return {
			label: `${start} - ${end}`,
			value: s.id,
			data: s,
		};
	});
};

export const mapOccupationsForDropdown = (occupations = [], companyDepartments) => {
	return occupations.map((o) => {
		const { roles = [], departments = [], pensions = [], salaries = [] } = o;

		const currentFilter = (o) => {
			return o.start <= today && (o.end >= today || !o.end);
		};

		const [activeRole] = roles.filter(currentFilter);
		const [activeDepartment] = departments.filter(currentFilter);

		const earliestRole = orderBy(roles, "start", "asc")[0];
		const earliestDepartment = orderBy(departments, "start", "asc")[0];
		const earliestPension = orderBy(pensions, "start", "asc")[0];
		const earliestSalary = orderBy(salaries, "start", "asc")[0];

		const latestRole = orderBy(roles, "end", "desc")[0];
		const latestDepartment = orderBy(departments, "end", "desc")[0];
		const latestPension = orderBy(pensions, "end", "desc")[0];
		const latestSalary = orderBy(salaries, "end", "desc")[0];

		const start = orderBy([earliestRole, earliestDepartment, earliestPension, earliestSalary], "start", "asc")[0]
			?.start;
		const end = orderBy([latestRole, latestDepartment, latestPension, latestSalary], "end", "a")[0]?.end;

		const departmentName =
			companyDepartments?.find(({ id }) => id == (activeDepartment || latestDepartment)?.department)?.name ||
			"No Department";

		const roleName = (activeRole || latestRole)?.name || "Untitled Role";

		return {
			active: start <= today && (end >= today || !end),
			start,
			end: end === "null" ? undefined : end,
			occupation: o.occupation,
			value: o.occupation,
			role: roleName,
			department: departmentName,
			label: `${roleName} | ${departmentName}`,
		};
	});
};
